import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Caption, CheckBoxCard, SubHeadingOne } from 'components/core';
import { useRecoilState } from 'recoil';
import { Role, rolesState, dashboardRolesState, currentDashboardState, Dashboard } from 'states';
import { useRolesService } from 'services';
import styles from './DashboardTabs.module.css';
import { FieldTooltip } from 'molecules/fieldTooltip/FieldTooltip';

interface AccessProps {
  register: any;
  setValue: any;
  formState: any;
  saveAttempted?: boolean;
}

export const Access = ({ register: _register, setValue: _setValue, formState: _formState, saveAttempted = false }: AccessProps) => {
  const { t } = useTranslation();
  const [roles, setRoles] = useRecoilState(rolesState);
  const roleService = useRolesService();
  const [currentDashboard] = useRecoilState(currentDashboardState);
  const [dashboardRoles, setDashboardRoles] = useRecoilState(dashboardRolesState);
  const [updateDashboard] = useState<Dashboard>(currentDashboard);
  const [accessRoles, setAccessRoles] = useState<any>(dashboardRoles);

  const getRoleClickHandler = (role: any) => () => updateRoles(role);

  useEffect(() => {
    getRoles();
  }, []);

  useEffect(() => {
    setAccessRoles(
      updateDashboard.data[0].access.escapedAccessConfig != null
        ? currentDashboard.data[0].access.escapedAccessConfig
        : updateDashboard.data[0].access.accessConfig
    );
    setDashboardRoles(accessRoles || []);
  }, [updateDashboard]);

  const getRoles = () => {
    roleService.getRoles().then((response) => {
      if (response.length > 0) setRoles(response);
    });
  };

  const isRoleSelected = (roleId: number) => {
    const currentDashboardRoles: Role[] = [...dashboardRoles];
    return currentDashboardRoles.findIndex((role) => role.id === roleId) !== -1;
  };

  const updateRoles = (selectedRole: Role) => {
    const currentDashboardRoles: Role[] = [...dashboardRoles];
    const idx = currentDashboardRoles.findIndex((role) => role.id === selectedRole.id);
    if (idx === -1) currentDashboardRoles.push(selectedRole);
    else currentDashboardRoles.splice(idx, 1);
    setDashboardRoles(currentDashboardRoles);
    setAccessRoles(currentDashboardRoles);
  };

  return (
    <div className="col-12 mb-3">
      <div className="mt-4">
        <div className={styles.fieldLabelRow}>
          <SubHeadingOne title="Access permission" />
          <FieldTooltip
            title="Access permission"
            description="Controls which platform roles can see and open this dashboard in the MIS Web App. Select all roles that should have access. Users assigned a role that is not selected here will not see this dashboard in their portal."
            note="Include Super Admin explicitly if Super Admin users need to see this dashboard."
          />
        </div>
      </div>

      <Caption text="Select roles that require access to this dashboard." />

      <div className="row">
        {roles.length > 0 &&
          roles.map((role: Role) => {
            return (
              <div className="col-sm-12 col-md-4 col-lg-4 mt-3" key={role.id}>
                <CheckBoxCard
                  label={role.name!}
                  clickHandler={getRoleClickHandler(role)}
                  isChecked={isRoleSelected(role.id!)}
                />
              </div>
            );
          })}
      </div>

      {/* Passive hint — no role selected, before save attempted */}
      {dashboardRoles.length === 0 && !saveAttempted && (
        <div className={`d-flex align-items-center gap-2 mt-3 ${styles.accessHint}`} role="note">
          <span className={`material-icons ${styles.accessHintIcon}`}>info</span>
          <span>{t('VALIDATION.ACCESS_ROLE_HINT')}</span>
        </div>
      )}

      {/* Hard error — after failed save with no roles selected */}
      {saveAttempted && dashboardRoles.length === 0 && (
        <div className={`d-flex align-items-center gap-2 mt-3 ${styles.accessError}`} role="alert">
          <span className={`material-icons ${styles.accessErrorIcon}`}>error_outline</span>
          <span>{t('VALIDATION.ACCESS_ROLE_REQUIRED')}</span>
        </div>
      )}
    </div>
  );
};